<script setup lang="ts">
import OT from "@opentok/client";
import { ref, nextTick } from "vue";
import { onClickOutside } from "@vueuse/core";

import type { iconName as iconNameType } from "@/Modules/App/Models/Styles/Icons";

import IconButton from "@/Components/Extend/TcIconButton.vue";

import DeviceSelector from "./DeviceSelector.vue";

import { waitFor } from "@/Utils/Promises";

interface ActionIconExtended {
  icon: iconNameType;
  active: boolean;
  disabled?: boolean;
  devices: (OT.Device | OT.AudioOutputDevice)[];
  selectedItem?: OT.Device | OT.AudioOutputDevice;
  canExtend?: boolean;
}

const ITEM_SELECTOR_SIZE = 52; // px

const emit = defineEmits<{
  (event: "toggleTrack"): void;
  (event: "select", value: OT.Device | OT.AudioOutputDevice): void;
  (event: "open"): void;
  (event: "close"): void;
}>();

const props = withDefaults(defineProps<ActionIconExtended>(), {
  disabled: false,
  paused: false,
  canExtend: true,
});

const showList = ref<boolean>(false);
const actionRef = ref<HTMLDivElement>();

const handleToggleShowList = () => {
  showList.value = !showList.value;

  if (showList.value) {
    emit("open");
  } else {
    emit("close");
  }
};

const handleSelectDevice = (d: OT.Device | OT.AudioOutputDevice) => {
  emit("select", d);
  emit("close");

  showList.value = false;
};

onClickOutside(actionRef, () => {
  if (showList.value) {
    emit("close");

    waitFor(0.1).then(() => {
      nextTick(() => {
        showList.value = false;
      });
    });
  }
});
</script>

<template>
  <div class="action">
    <DeviceSelector
      v-if="showList"
      id="audio_selector"
      class="action__selector"
      :devices="props.devices"
      :selected="props.selectedItem"
      :style="{
        top: `calc(-1 * (16px + ${ITEM_SELECTOR_SIZE * props.devices.length}px))`,
      }"
      @select="handleSelectDevice"
      @open="() => emit('open')"
      @close="() => emit('close')"
    />

    <div
      ref="actionRef"
      class="action__container"
      :class="{
        'action__container--disabled': props.disabled,
        'action__container--paused': !props.active,
      }"
    >
      <IconButton
        class="action__container__button"
        :color="props.active ? 'background-alt' : 'content-high'"
        :iconName="props.icon"
        @click="() => emit('toggleTrack')"
      />

      <IconButton
        v-if="props.canExtend"
        class="pl-1 pr-3"
        :iconName="showList ? 'ChevronUp' : 'ChevronDown'"
        color="background-alt"
        @click="handleToggleShowList"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.action {
  position: relative;

  &__selector {
    position: absolute;
    width: 300px;
    z-index: 1;
    left: 50%;
    transform: translateX(-50%);
  }

  &__container {
    @include sp.padding(false, 3);
    @include sp.margin(r, 4);
    @include colors.bg(content, high, true);

    display: flex;
    overflow: hidden;
    border-radius: 100px !important;
    padding: 0 !important;

    &--paused {
      .action__container {
        &__button {
          @include colors.bg(content, low-solid, true);
        }
      }
    }

    &__button {
      @include sp.padding(false, 3);

      border-radius: 100%;
    }
  }
}
</style>
